import React, { useState, useEffect } from "react";
import CustomError from "../../../../custom/components/Errors/CustomError";
import Asterisks from "../../../../custom/components/Asterisks/Asterisks";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";

const SingleSelectInput = ({
  label,
  options = [],
  value = "",
  onSelect,
  noDataMessage = "No data available",
  placeholder = "Select an option",
  parentClassName = "",
  customError,
  asterisk,
  dropdownId,
  activeDropdown,
  setActiveDropdown,
  loading,
}) => {
  const [selectedItem, setSelectedItem] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const skeletonStyle = {
    backgroundColor: "#f0f0f0",
    borderRadius: "4px",
    position: "relative",
    overflow: "hidden",
    background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.5s infinite linear",
  };
  const styles = {
    dropdownContainer: {
      position: "relative",
      width: "100%",
      marginTop: "30px",
    },
    floatingLabel: {
      position: "absolute",
      top: isOpen || selectedItem ? "-8px" : "50%",
      left: "4px",
      transform: isOpen || selectedItem ? "translateY(0)" : "translateY(-50%)",
      fontSize: isOpen || selectedItem ? "9px" : "12px",
      color: isOpen || selectedItem ? "#007bff" : "#6c757d",
      transition: "all 0.3s ease",
      background: "white",
      padding: "0 5px",
      pointerEvents: "none",
      zIndex: 1,
    },
    dropdownToggle: {
      width: "100%",
      padding: "12px",
      textAlign: "left",
      borderRadius: "5px",
      background: "white",
      border: selectedItem ? "1px solid #007bff" : "1px solid #ced4da",
    },
    dropdownMenu: {
      width: "160%",
      maxHeight: "300px",
      overflowY: "auto",
      whiteSpace: "normal",
      wordWrap: "break-word",
      wordBreak: "break-word",
      position: "absolute",
      left: "-3rem",
      zIndex: 1000,
      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
      paddingTop: 0,
      background: "white",
    },
    searchInput: {
      padding: "8px",
      margin: "0 8px 8px 8px",
      borderRadius: "4px",
      border: "1px solid #ced4da",
      width: "calc(100% - 16px)",
    },
    checkboxContainer: {
      display: "flex",
      alignItems: "center",
      padding: "8px 16px",
    },
  };

  useEffect(() => {
    setSelectedItem(value?.value);
  }, [value]);

  const toggleDropdown = () => {
    if (isOpen) {
      setIsOpen(false);
      setActiveDropdown(null);
    } else {
      setIsOpen(true);
      setActiveDropdown(dropdownId);
    }
  };

  const handleOptionChange = (option) => {
    setSelectedItem(option.value);
    onSelect(option);
  };

  useEffect(() => {
    if (activeDropdown !== dropdownId) {
      setIsOpen(false);
    }
  }, [activeDropdown, dropdownId]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (!event.target.closest(".custom-dropdown-container")) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );
  if (loading) {
    return (
      <div
        className={`${parentClassName} custom-dropdown-container`}
        style={styles.dropdownContainer}
      >
        <style>
          {`
            @keyframes shimmer {
              to { background-position: -200% 0; }
            }
          `}
        </style>

        <div
          style={{
            ...skeletonStyle,
            width: "100%",
            height: "40px",
            borderRadius: "5px",
          }}
        ></div>
      </div>
    );
  }
  return (
    <div
      className={`${parentClassName} custom-dropdown-container`}
      style={styles.dropdownContainer}
    >
      <label style={styles.floatingLabel}>
        {label} <Asterisks asterisk={asterisk} />
      </label>
      <button
        className="btn dropdown-toggle d-flex justify-content-between align-items-center"
        type="button"
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        style={styles.dropdownToggle}
      >
        <div
          style={{
            display: "block",
            width: "100%",
            maxWidth: "300px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            color: "black",
          }}
        >
          {selectedItem
            ? options.find((opt) => opt.value === selectedItem)?.label
            : placeholder}{" "}
        </div>
        <span className="ms-2">
          <i className={`bi bi-caret-${isOpen ? "up" : "down"}-fill`}></i>
        </span>
      </button>
      {isOpen && (
        <ul className="dropdown-menu show" style={styles.dropdownMenu}>
          <div
            style={{
              position: "sticky",
              top: "0px",
              background: "white",
              zIndex: 10,
              paddingBottom: "5px",
              borderBottom: "1px solid #ddd",
              paddingTop: "5px",
            }}
          >
            <li className="dropdown-item d-flex justify-content-center">
              <h5 className="font-size-12 mt-0 mb-0">{label}</h5>
            </li>
            <input
              type="text"
              className="form-control m-2"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: "91%" }}
            />
          </div>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <li key={option.value} onClick={(e) => e.stopPropagation()}>
                <label
                  className="dropdown-item"
                  style={{
                    display: "flex",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    wordBreak: "break-word",
                    padding: "8px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedItem == option.value}
                    onChange={() => handleOptionChange(option)}
                    className="form-check-input me-2"
                  />
                  {option.label}
                </label>
              </li>
            ))
          ) : (
            <li className="dropdown-item text-muted">{noDataMessage}</li>
          )}
        </ul>
      )}
      {customError && <CustomError error={customError} />}
    </div>
  );
};

export default SingleSelectInput;
